import React, { useState, useEffect, useCallback } from "react";
import {
  fetchCards, cardsFromPayload, normalizeFromPPT, cardNumberMatches,
  fetchCardEbayData, psaPricesFromEbayPayload, formatUSD, CONDITIONS, PSA_GRADES,
  fetchOnePiecePrices,
} from "../../shared";
import { searchOnePiece, baseCode, printLabel, printsOf } from "../onepiece/onePieceIndex";
import { opImageUrl } from "../onepiece/onePieceCards";
import { CardZoom, AddToListButton, CardLinks } from "../../ui";

const PRESETS = [70, 75, 80, 85, 90];
const PCT_MIN = 70;
const PCT_MAX = 90;

/* One card, looked at properly: what it's worth raw, and what the graded
   copies are selling for beside it — which is the question "should I grade
   this?" actually needs answered. */
export default function SingleCard({
  addToCollection, addToWantList, isOnWantList, isInCollection,
  wantLists = [], listNameFor, isOnePiece = false,
  /* A card handed over by the search that already found it, instead of a name
     and a number typed in again. This screen used to be reachable only by
     retyping what you had just searched for — and on Pokemon that retyping
     spent a second billed lookup to arrive at a card the app was already
     holding. Given one, everything up to "which card is this" is skipped. */
  startWith = null,
  onBack = null,
}) {
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [pct, setPct] = useState(80);
  const [card, setCard] = useState(null);
  const [condition, setCondition] = useState("Near Mint");
  const [isSlab, setIsSlab] = useState(false);
  const [psaGrade, setPsaGrade] = useState(10);
  const [comps, setComps] = useState(null);      // { 1..10 } of eBay sold averages
  const [compsState, setCompsState] = useState("idle");
  const [compsError, setCompsError] = useState("");
  const [needsNumber, setNeedsNumber] = useState(false);
  const [opPrints, setOpPrints] = useState([]);   // other prints of the same card number
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(false);
  const [showLookup, setShowLookup] = useState(false);
  // The owner's figure for a condition nobody quotes. Per card looked at.
  const [ownPrice, setOwnPrice] = useState("");

  /* A One Piece code names one card on its own — OP06-022 needs no number
     beside it, and demanding one for something already unambiguous is just a
     locked door. A Pokemon name never identifies a print by itself. */
  const looksLikeCode = /^[A-Za-z]+\d*-\d+/.test(name.trim());

  /* Both of these used to live inside priceIt, which meant arriving here with
     a card already in hand had no way to reach them without pretending to
     search for it. They are the part that is about a known card rather than
     about finding one. */

  /* Every print of the one card number, priced together. The prints of a card
     differ by as much as 300x, so the alternatives have to be there to switch
     to — and this costs no lookup either way, the catalogue is bundled. */
  const showOpPrints = useCallback(async (hit) => {
    const family = printsOf(hit.id);
    const prices = await fetchOnePiecePrices(family.map((p) => p.id));
    const options = family.map((p) => ({
      id: p.id,
      label: printLabel(p.id, p.rarity),
      market: Number(prices[p.id]),
      image: opImageUrl(p.id),
    }));
    setOpPrints(options);
    const chosen = options.find((p) => p.id === hit.id) || options[0];
    setCard({
      id: chosen.id,
      name: hit.name,
      number: baseCode(hit.id),
      setName: chosen.label,
      imageUrl: chosen.image,
      prices: {},
      marketPrice: Number.isFinite(chosen.market) ? chosen.market : null,
    });
    setCompsState("idle");
  }, []);

  // Graded sales are a second call, so it runs after the card is on screen
  // rather than holding the whole result behind it.
  const loadComps = useCallback(async (id) => {
    setCompsState("loading");
    try {
      const found = psaPricesFromEbayPayload(await fetchCardEbayData(id));
      setComps(found);
      setCompsState(found ? "ready" : "none");
    } catch (e) {
      /* "None found" used to cover this too, which reads as "this card has
         never sold graded" when the truth is usually that the graded source
         is out of credits for the day. Different facts, different words. */
      const status = e?.response?.status;
      setCompsError(
        status === 429
          ? "out of credits until 00:00 UTC"
          : status === 404
          ? "no graded listing for this print"
          : `lookup failed${status ? ` (${status})` : ""}`
      );
      setCompsState("error");
    }
  }, []);

  /* Handed a card, show it. No search, and on Pokemon no billed call — the
     card the search already paid for is the card that gets shown. */
  useEffect(() => {
    if (!startWith) return undefined;
    let cancelled = false;

    (async () => {
      setError("");
      setNeedsNumber(false);
      setComps(null);
      setCompsError("");
      setOpPrints([]);
      setCondition("Near Mint");
      setIsSlab(false);
      // The fields still fill in, so the screen can be edited from here rather
      // than being a dead end you have to leave to change anything.
      setName(startWith.name || "");
      setNumber(String(startWith.displayNumber ?? startWith.number ?? ""));
      setLoading(true);
      try {
        if (startWith.don) {
          /* A DON!! card is already everything it is going to be: no other
             prints, because it has no card number for there to be prints of,
             and no graded sales, because One Piece has none anywhere free. It
             arrived priced, so there is nothing left to ask anybody. */
          setCard({
            ...startWith,
            number: startWith.displayNumber || startWith.setName || "DON!!",
            prices: startWith.prices || {},
          });
          setCompsState("idle");
        } else if (isOnePiece) {
          await showOpPrints({ id: startWith.id, name: startWith.name });
        } else {
          setCard(startWith);
          await loadComps(startWith.id);
        }
      } catch (e) {
        if (!cancelled) setError(`Lookup failed: ${e?.message || e}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [startWith, isOnePiece, showOpPrints, loadComps]);

  // Otherwise both fields. This screen answers "what is this exact card
  // worth", and a name alone can't identify a print — a bare "Charizard ex"
  // matches dozens worth anywhere from a dollar to hundreds. The guess is what
  // quietly returned the wrong card.
  async function priceIt() {
    /* The button stays live and the explanation waits until you ask. Greying
       it out and explaining up front told everyone off for a mistake most had
       not made yet. */
    if (!name.trim()) {
      setError("Enter a card name.");
      return;
    }
    if (!number.trim() && !(isOnePiece && looksLikeCode)) {
      setNeedsNumber(true);
      return;
    }
    setNeedsNumber(false);
    setLoading(true);

    /* One Piece resolves against the catalogue bundled into the app, so this
       costs one price call and no lookup at all. There are no graded sales for
       the game anywhere free, so the comps ladder is simply absent rather than
       shown empty. */
    if (isOnePiece) {
      setError("");
      setCard(null);
      try {
        const q = `${name.trim()} ${number.trim()}`.trim();
        let hits = searchOnePiece(q);
        if (!hits.length) hits = searchOnePiece(name.trim());
        if (number.trim() && hits.length) {
          const want = String(parseInt(number.trim(), 10));
          const narrowed = hits.filter((h) => {
            const printed = baseCode(h.id).split("-")[1];
            return printed && String(parseInt(printed, 10)) === want;
          });
          if (narrowed.length) hits = narrowed;
        }
        if (!hits.length) {
          setError(`Nothing matched "${q}". Try the code printed on the card, like OP01-121.`);
          return;
        }
        /* Same hazard as the stack: a card number names a card, not a print,
           and they can differ by 300x. Every print is priced in the one call
           so the alternatives are there to switch to. */
        await showOpPrints(hits[0]);
      } catch (e) {
        setError(`Lookup failed: ${e?.response?.data?.message || e.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }
    setError("");
    setCard(null);
    setComps(null);
    setCompsState("idle");
    setCompsError("");
    setOpPrints([]);
    try {
      // The API has no number parameter — it rejects one — but putting the
      // number in the query ranks the exact print first, which is far cheaper
      // than pulling a wide page and filtering. Billing is per card returned.
      const payload = await fetchCards({ search: `${name.trim()} ${number.trim()}`, limit: 10 });
      const found = cardsFromPayload(payload).map(normalizeFromPPT).filter(Boolean);

      const matched = found.find((c) => cardNumberMatches(number.trim(), c.number));

      // Falling back to the first result is how "Charizard ex 199" quietly
      // became XY29. Say it didn't match instead.
      if (!matched) {
        setError(
          `No "${name.trim()}" with number ${number.trim()}. Check the number — it's the one before the slash, bottom-left of the card.`
        );
        return;
      }
      const hit = matched;
      setCard(hit);
      setCondition("Near Mint");
      setIsSlab(false);
      await loadComps(hit.id);
    } catch (e) {
      const status = e?.response?.status;
      setError(
        status === 429
          ? "Out of API credits or rate limited — the daily allowance resets at 00:00 UTC."
          : `Lookup failed: ${e?.response?.data?.message || e.message}`
      );
    } finally {
      setLoading(false);
    }
  }

  /* A quote for the chosen condition, or the owner's own figure, or — for Near
     Mint only — the market price. Anything else is unpriced, and the screen
     asks rather than scales: the percentage-of-NM it used to show here was
     checked against a Charizard and found wrong. */
  const quoted = card?.prices?.[condition];
  const quotedPrice = quoted && quoted.estimated !== true ? Number(quoted.price) : NaN;
  const marketNM = Number(card?.marketPrice);
  const own = Number(ownPrice);
  const rawPrice = Number.isFinite(quotedPrice) && quotedPrice > 0
    ? quotedPrice
    : Number.isFinite(own) && own > 0
    ? own
    : condition === "Near Mint" && Number.isFinite(marketNM) && marketNM > 0
    ? marketNM
    : null;
  const gradedPrice = comps && Number(comps[psaGrade]);
  const market = isSlab && Number.isFinite(gradedPrice) ? gradedPrice : rawPrice;
  const unpriced = card && !isSlab && rawPrice === null;
  const isOwn = !isSlab && rawPrice !== null && !(Number.isFinite(quotedPrice) && quotedPrice > 0) && Number.isFinite(own) && own > 0;

  // The ladder the mockup asks for: raw beside the grades worth chasing.
  const ladder = [
    { label: "RAW NM", value: card ? Number(card.prices?.["Near Mint"]?.price ?? card.marketPrice) : null, bright: true },
    { label: "PSA 8",  value: comps?.[8],  bright: false },
    { label: "PSA 9",  value: comps?.[9],  bright: false },
    { label: "PSA 10", value: comps?.[10], bright: true },
  ];

  const forList = card && {
    ...card, condition, isSlab, psaGrade,
    psaPrices: comps || null,
    // The figure typed here travels with the card into the collection.
    ...(isOwn ? { ownPrice: own } : {}),
  };

  return (
    <div className="single">
      {/* The two games look identical here otherwise — same two boxes, same
          button — so nothing told you the switch had taken effect. */}
      {/* Arriving from a search means there is a list of results behind this
          one card, and no browser back button to return to it — the app is one
          page. Without this, looking at a second result meant searching
          again, which on Pokemon is a billed call to find what was already
          found. */}
      {onBack && (
        <button type="button" className="btn btn-secondary btn-sm single-back" onClick={onBack}>
          ← Back to search
        </button>
      )}

      {/* Two ways in, two things to call it. Typed into from the start, this is
          a lookup and says so. Arrived at by clicking a card, it is that card's
          page — and titling it "Single card" over two boxes you did not fill in
          made it read as a form you had failed to complete rather than as the
          answer to what you just clicked. */}
      <h2 className="section-title">
        {startWith ? startWith.name : "Single card"}
        <span className="section-game">{isOnePiece ? "One Piece" : "Pokémon"}</span>
      </h2>

      {/* Kept, but out of the way of a card you did not ask to look up. It
          stays in the page rather than being removed, because changing the
          number is the obvious next thing to want and sending someone back to
          Search to do it would undo the point of arriving here. */}
      {startWith && (
        <button
          type="button"
          className="btn btn-link single-lookup-toggle"
          onClick={() => setShowLookup((v) => !v)}
          aria-expanded={showLookup}
        >
          {showLookup ? "Hide lookup" : "Look up a different card"}
        </button>
      )}

      <div className="single-search" hidden={Boolean(startWith) && !showLookup}>
        <input
          className="single-input"
          type="text"
          placeholder={isOnePiece ? "Card name, or a code like OP06-022" : "Card name"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") priceIt(); }}
          aria-label="Card name"
        />
        <input
          className="single-input single-input--num"
          type="text"
          placeholder={isOnePiece && looksLikeCode ? "Not needed" : "Card # (required)"}
          value={number}
          onChange={(e) => { setNumber(e.target.value); setNeedsNumber(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") priceIt(); }}
          aria-label="Card number"
          required
        />
        <button className="btn btn-primary single-go" onClick={priceIt} disabled={loading}>
          {loading ? "Pricing…" : "Price it"}
        </button>
      </div>

      {/* Only once you've actually tried without one. */}
      {needsNumber && (
        <p className="single-hint">
          {isOnePiece
            ? "Add the number, or paste the code from the bottom of the card — OP06-022. A One Piece name on its own matches dozens of cards."
            : "Add the card number — it's the one before the slash, bottom-left of the card. Without it there's no telling which print you mean."}
        </p>
      )}

      {error && <p className="msg-error">{error}</p>}

      {card && (
        <div className="single-body">
          <button
            type="button"
            className="single-art card-zoom-btn"
            onClick={() => setZoom(true)}
            aria-label={`Enlarge ${card.name}`}
            title={`Enlarge ${card.name}`}
          >
            {card.imageUrl
              ? <img src={card.imageUrl} alt={card.name} />
              : <span className="single-art-empty">CARD ART</span>}
          </button>

          <div className="single-details">
            <div>
              <div className="single-meta">
                {card.setName} {card.number && `· ${card.number}`}
              </div>
              <div className="single-name">{card.name}</div>
              <CardLinks card={card} game={isOnePiece ? "onepiece" : "pokemon"} />
            </div>

            {/* A card number names a card, not a print, and the prints of one
                can differ by 300x. There is room here to show them all rather
                than hide them behind a chip like the stack table does. */}
            {isOnePiece && opPrints.length > 1 && (
              <div className="op-prints">
                <div className="single-label">
                  {opPrints.length} PRINTS OF THIS CARD · PICK THE ONE YOU'RE HOLDING
                </div>
                <div className="op-prints-row">
                  {opPrints.map((p) => (
                    <button
                      key={p.id}
                      className={"op-print" + (p.id === card.id ? " op-print--on" : "")}
                      aria-pressed={p.id === card.id}
                      onClick={() =>
                        setCard((c) => ({
                          ...c,
                          id: p.id,
                          setName: p.label,
                          imageUrl: p.image,
                          marketPrice: Number.isFinite(p.market) ? p.market : null,
                        }))
                      }
                    >
                      <img src={p.image} alt={p.label} loading="lazy" />
                      <span className="op-print-label">{p.label}</span>
                      <span className="op-print-price">
                        {Number.isFinite(p.market) ? formatUSD(p.market) : "no price"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nothing free carries graded One Piece sales, and the game has
                no condition pricing either — so these controls are absent
                rather than present and inert. */}
            {!isOnePiece && (
            <div className="single-modes">
              <button
                className={"col-seg" + (!isSlab ? " col-seg--on" : "")}
                onClick={() => setIsSlab(false)}
              >
                Raw card
              </button>
              <button
                className={"col-seg" + (isSlab ? " col-seg--on" : "")}
                onClick={() => setIsSlab(true)}
              >
                PSA slab
              </button>
              {isSlab ? (
                <select
                  className="col-seg single-select"
                  value={psaGrade}
                  onChange={(e) => setPsaGrade(Number(e.target.value))}
                  aria-label="PSA grade"
                >
                  {PSA_GRADES.slice().reverse().map((g) => (
                    <option key={g} value={g}>PSA {g}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="col-seg single-select"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  aria-label="Condition"
                >
                  {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
            )}

            <div className="single-figures">
              <div>
                <div className="single-label">MARKET</div>
                {unpriced ? (
                  /* No quote below Near Mint from any source this app reads,
                     and no guess offered in its place. Asked instead. */
                  <label className="single-market single-market--ask">
                    <span className="single-ask-label">No {condition} quote — your price</span>
                    <span className="card-price-ask-field">
                      <span aria-hidden="true">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="card-price-input"
                        placeholder="0.00"
                        value={ownPrice}
                        onChange={(e) => setOwnPrice(e.target.value.replace(/[^\d.]/g, ""))}
                        aria-label={`Your price in ${condition} condition`}
                      />
                    </span>
                  </label>
                ) : (
                  <div className="single-market">
                    {formatUSD(market)}
                    {isOwn && (
                      <button
                        type="button"
                        className="price-estimated price-own"
                        title="A price you set. Click to change it."
                        onClick={() => setOwnPrice("")}
                      >
                        YOURS
                      </button>
                    )}
                  </div>
                )}
                {/* "No sales at this grade" is a claim about the card. It must
                    not stand in for "we couldn't ask". */}
                {isSlab && !Number.isFinite(gradedPrice) && (
                  <div className="single-note">
                    {compsState === "loading"
                      ? "CHECKING GRADED SALES…"
                      : compsState === "error"
                      ? `GRADED PRICES UNAVAILABLE · ${compsError.toUpperCase()}`
                      : `NO EBAY SALES AT PSA ${psaGrade}`}
                  </div>
                )}
              </div>
              <div>
                <div className="single-label single-label--accent">
                  WORTH AT
                  <input
                    className="single-pct-input"
                    type="text"
                    inputMode="numeric"
                    value={pct}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, "");
                      setPct(v === "" ? 0 : Math.min(200, Number(v)));
                    }}
                    onBlur={() => { if (!pct) setPct(PCT_MIN); }}
                    aria-label="Percentage of market"
                    size={3}
                  />%
                </div>
                <div className="single-payout">
                  {market === null ? "—" : formatUSD(market * (pct / 100))}
                </div>
              </div>
            </div>

            <div className="single-pct">
              <input
                className="stack-slider"
                type="range"
                min={PCT_MIN} max={PCT_MAX} step={5}
                value={Math.min(PCT_MAX, Math.max(PCT_MIN, pct))}
                onChange={(e) => setPct(Number(e.target.value))}
                aria-label="Percentage of market slider"
              />
              <div className="stack-presets">
                {PRESETS.map((v) => (
                  <button
                    key={v}
                    className={"stack-preset" + (v === pct ? " stack-preset--active" : "")}
                    aria-pressed={v === pct}
                    onClick={() => setPct(v)}
                  >
                    {v}%
                  </button>
                ))}
              </div>
            </div>

            {!isOnePiece && (
            <div>
              <div className="single-label single-comps-head">
                GRADED COMPS · EBAY SOLD
                {compsState === "loading" && " · LOADING"}
                {compsState === "none" && " · NONE FOUND"}
                {compsState === "error" && (
                  <span className="single-comps-err"> · {compsError.toUpperCase()}</span>
                )}
              </div>
              <div className="single-ladder">
                {ladder.map((g) => (
                  <div className="single-rung" key={g.label}>
                    <div className="single-rung-label">{g.label}</div>
                    <div className={"single-rung-price" + (g.bright ? "" : " single-rung-price--dim")}>
                      {Number.isFinite(Number(g.value)) ? formatUSD(Number(g.value)) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            <div className="single-actions">
              {/* Both buttons say what already happened. Adding a card that's
                  already there is a no-op, and silently doing nothing reads as
                  a broken button. */}
              <button
                className="btn btn-add"
                onClick={() => addToCollection(forList)}
                disabled={isInCollection?.(card.id)}
              >
                {isInCollection?.(card.id) ? "In collection" : "Add to collection"}
              </button>
              <AddToListButton
                lists={wantLists}
                onAdd={(listId) => addToWantList(forList, listId)}
                onList={isOnWantList?.(card.id)}
                onListName={listNameFor?.(card.id)}
                label="Add to want list"
                className="btn btn-secondary"
              />
            </div>
          </div>
        </div>
      )}

      <CardZoom card={zoom ? card : null} onClose={() => setZoom(false)} />
    </div>
  );
}
